import { FC, useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import { useInView } from 'react-intersection-observer';
import { AsyncThunk } from '@reduxjs/toolkit';

import { PrimaryMarketFilter } from './PrimaryMarketFilter';
import { PrimaryMarketItem } from './PrimaryMarketItem';
import { EmptyComponent, Loading, RejectedMessage } from 'componets/ui';
import { AgreeWithTerms } from 'componets/ui/AgreeWithTerms';

import { getPrimaryMarketItems, getPrimaryMarketItemsMore } from 'features/primaryMarket/actions';

import { useAppDispatch, useAppSelector, useDebouncedChange, useInitialSearchParams } from 'hooks';

import { FieldData, PaginationParams } from 'models';

import { PAGE_SIZE, RequestStatus } from 'utils/constants';

import { selectUser } from 'selectors';

import './index.scss';

let pageSize = PAGE_SIZE;

if (window.innerWidth < 1580 && window.innerWidth > 1267) {
	pageSize = PAGE_SIZE + 1;
}

export const PrimaryMarket: FC = () => {
	const { ref, inView } = useInView({
		threshold: 1,
	});

	const { items, status: loading, pager } = useAppSelector((state) => state.primaryMarket);

	const user = useAppSelector(selectUser);

	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const watchedValues = Form.useWatch([], form);
	const { values } = useDebouncedChange(watchedValues);

	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const isLoading = loading === RequestStatus.PENDING;
	const dispatch = useAppDispatch();

	const onGetItems = useCallback(
		(data: PaginationParams, callback: AsyncThunk<any, PaginationParams | undefined, {}>) => {
			dispatch(callback(data))
				.unwrap()
				.finally(() => {
					setIsLoadingMore(false);
				});
		},
		[dispatch],
	);

	const { initialValues } = useInitialSearchParams({ values, user });

	useEffect(() => {
		if (inView && !isLoadingMore && pager.currentPage < pager.pageCount) {
			setIsLoadingMore(true);
		}
	}, [inView, isLoadingMore, pager]);

	useEffect(() => {
		if (isLoadingMore && pager.currentPage < pager.pageCount && !isLoading) {
			onGetItems(
				{ page: pager.currentPage + 1, pageSize, ...initialValues },
				getPrimaryMarketItemsMore,
			);
		}
	}, [isLoading, isLoadingMore, onGetItems, pager.currentPage, pager.pageCount, initialValues]);

	useEffect(() => {
		const isNotValid =
			initialValues['interestRate[from]']?.endsWith('.') ||
			initialValues['interestRate[to]']?.endsWith('.') ||
			+initialValues['interestRate[from]'] > +initialValues['interestRate[to]'];

		if (isNotValid) {
			return;
		}

		onGetItems({ pageSize, page: 1, ...initialValues }, getPrimaryMarketItems);
	}, [onGetItems, initialValues]);

	return (
		<>
			{loading === RequestStatus.REJECTED ? (
				<RejectedMessage />
			) : (
				<div className="primaryMarket">
					<PrimaryMarketFilter
						form={form}
						setFields={setFields}
						fields={fields}
						values={values}
						initialValues={initialValues}
					/>
					{loading === RequestStatus.FULFILLED && (
						<>
							{items.length === 0 ? (
								<EmptyComponent decription="projectsWillAvailableSoonText" />
							) : (
								<div className="primaryMarket__items">
									{items.map((item) => (
										<PrimaryMarketItem key={item.id} item={item} />
									))}
								</div>
							)}

							{items.length > 0 && <div ref={ref} className="primaryMarket__bottom" />}
						</>
					)}

					{isLoading && <Loading />}
				</div>
			)}

			{user?.accountId && !user.agreeWithTerms && <AgreeWithTerms />}
		</>
	);
};
